import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react'

// A tiny client-side toast system. There was no notification channel before this — server
// feedback only ever arrived as a flash banner after a full Inertia visit. Optimistic mutations
// (see lib/useServerState) need a way to say "that didn't save" without a round-trip, so this
// provides an imperative `toast.error(...) / toast.success(...)` usable from any page.

type ToastKind = 'error' | 'success' | 'info'
type Toast = { id: number; kind: ToastKind; message: string }

type ToastApi = {
  error: (message: string) => void
  success: (message: string) => void
  info: (message: string) => void
}

const ToastCtx = createContext<ToastApi | null>(null)

// Falls back to a no-op console warning if used outside the provider (e.g. auth screens that
// aren't wrapped) so a stray call can never crash a page.
export function useToast(): ToastApi {
  return (
    useContext(ToastCtx) ?? {
      error: (m) => console.warn('[toast:error]', m),
      success: (m) => console.warn('[toast:success]', m),
      info: (m) => console.warn('[toast:info]', m),
    }
  )
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const nextId = useRef(1)
  const timers = useRef<Record<number, ReturnType<typeof setTimeout>>>({})

  const dismiss = useCallback((id: number) => {
    setToasts((cur) => cur.filter((x) => x.id !== id))
    clearTimeout(timers.current[id])
    delete timers.current[id]
  }, [])

  const push = useCallback(
    (kind: ToastKind, message: string) => {
      const id = nextId.current++
      setToasts((cur) => [...cur, { id, kind, message }])
      timers.current[id] = setTimeout(() => dismiss(id), kind === 'error' ? 6000 : 3500)
    },
    [dismiss],
  )

  const api = useRef<ToastApi>({
    error: (m) => push('error', m),
    success: (m) => push('success', m),
    info: (m) => push('info', m),
  }).current

  return (
    <ToastCtx.Provider value={api}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[100] flex flex-col items-center gap-2 p-4 sm:items-end sm:p-6">
        {toasts.map((toast) => (
          <ToastRow key={toast.id} toast={toast} onDismiss={() => dismiss(toast.id)} />
        ))}
      </div>
    </ToastCtx.Provider>
  )
}

function ToastRow({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const tone =
    toast.kind === 'error'
      ? 'border-red-200 bg-red-50 text-red-800'
      : toast.kind === 'success'
        ? 'border-teal/30 bg-teal/10 text-teal'
        : 'border-line bg-surface text-ink'
  const dot = toast.kind === 'error' ? 'bg-red-500' : toast.kind === 'success' ? 'bg-teal' : 'bg-ember'
  return (
    <div
      role="status"
      onClick={onDismiss}
      className={`pointer-events-auto flex max-w-sm cursor-pointer items-start gap-2.5 rounded-xl border px-3.5 py-2.5 text-[13px] font-medium shadow-lg ${tone}`}
    >
      <span className={`mt-1 h-1.5 w-1.5 flex-none rounded-full ${dot}`} />
      <span className="leading-snug">{toast.message}</span>
    </div>
  )
}
